import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../stores/useAppStore';
import './routes.css';

export default function UniversesRoute() {
  const universes = useAppStore((s) => s.universes);
  const stories = useAppStore((s) => s.stories);
  const slugPool = useAppStore((s) => s.slugPool);
  const createUniverse = useAppStore((s) => s.createUniverse);
  const setActiveUniverse = useAppStore((s) => s.setActiveUniverse);
  const nav = useNavigate();

  const [creating, setCreating] = useState(false);
  const [draftName, setDraftName] = useState('');

  const visible = useMemo(
    () =>
      Object.values(universes)
        .filter((u) => !u.hidden)
        .sort((a, b) => b.updatedAt - a.updatedAt),
    [universes]
  );

  function counts(universeId: string) {
    let storyCount = 0;
    for (const story of Object.values(stories)) {
      if (story.universeId === universeId && !story.hidden) storyCount++;
    }
    let slugCount = 0;
    for (const slug of slugPool) {
      if (slug.universeId === universeId && !slug.hidden) slugCount++;
    }
    return { storyCount, slugCount };
  }

  function enter(universeId: string) {
    setActiveUniverse(universeId);
    nav('/slugs');
  }

  function commitNew() {
    const name = draftName.trim();
    if (!name) return;
    const u = createUniverse({ name });
    setActiveUniverse(u.id);
    setCreating(false);
    setDraftName('');
    nav('/universe-settings');
  }

  return (
    <div className="route">
      <h1>Story universes</h1>
      <div className="dim small">Each universe has its own characters, voice, and prompt templates.</div>

      <div className="slug-list">
        {visible.length === 0 && !creating && (
          <div className="dim">No universes yet. Create one to get started.</div>
        )}
        {visible.map((u) => {
          const { storyCount, slugCount } = counts(u.id);
          return (
            <button key={u.id} className="story-picker-row" onClick={() => enter(u.id)}>
              <div>
                <div className="slug-title">{u.name}</div>
                {u.description && <div className="dim small">{u.description}</div>}
                <div className="dim small">
                  {storyCount} {storyCount === 1 ? 'story' : 'stories'} · {slugCount} {slugCount === 1 ? 'slug' : 'slugs'}
                </div>
              </div>
              <span className="dim">›</span>
            </button>
          );
        })}
      </div>

      <div className="card">
        {creating ? (
          <>
            <label>
              <div className="label">New universe name</div>
              <input
                autoFocus
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitNew();
                  if (e.key === 'Escape') {
                    setCreating(false);
                    setDraftName('');
                  }
                }}
                placeholder="e.g., Two Pirate Cats"
              />
            </label>
            <div className="row">
              <button
                className="ghost"
                onClick={() => {
                  setCreating(false);
                  setDraftName('');
                }}
              >
                Cancel
              </button>
              <div className="spacer" />
              <button
                className="primary"
                onClick={commitNew}
                disabled={!draftName.trim()}
              >
                Create
              </button>
            </div>
          </>
        ) : (
          <button className="primary" onClick={() => setCreating(true)}>
            + New universe
          </button>
        )}
      </div>
    </div>
  );
}
